import {
  battingHandShort,
  bowlingStyleShort,
  formatBatterStatus,
  formatBowling,
  formatExtras,
  formatFallOfWicket,
  formatOvers,
  formatRate,
  formatScore,
} from "@sportsos/engine";
import type { InningsState, MatchState, PlayerId } from "@sportsos/engine";

interface Props {
  readonly state: MatchState;
  readonly name: (playerId: PlayerId) => string;
  readonly teamName: (teamId: InningsState["battingTeamId"]) => string;
  readonly open?: boolean;
  /**
   * Print "LHB" beside a batter and "SLA" beside a bowler (ADR-0023). Off by default: the scorer's
   * read half is frozen (UI-PRD §4.3) and this card is shared with it, so only Match Detail asks.
   * Nothing is printed for a player whose hand or style the ledger never recorded.
   */
  readonly showStyle?: boolean;
}

const scrollWrap: React.CSSProperties = {
  overflowX: "auto",
  WebkitOverflowScrolling: "touch",
  marginLeft: "calc(-1 * var(--ds-space-4))",
  marginRight: "calc(-1 * var(--ds-space-4))",
  paddingLeft: "var(--ds-space-4)",
  paddingRight: "var(--ds-space-4)",
};

const styleTag: React.CSSProperties = {
  fontSize: "var(--ds-text-2xs)",
  color: "var(--ds-text-muted)",
  fontWeight: 600,
  marginLeft: "var(--ds-space-1)",
};

function Innings({
  innings,
  name,
  teamName,
  showStyle,
}: {
  readonly innings: InningsState;
  readonly name: (playerId: PlayerId) => string;
  readonly teamName: (teamId: InningsState["battingTeamId"]) => string;
  readonly showStyle: boolean;
}): React.ReactElement {
  const batted = innings.batting.filter((line) => line.batted);
  const didNotBat = innings.batting.filter((line) => !line.batted);
  const bowled = innings.bowling.filter((line) => line.overs.completed > 0 || line.overs.balls > 0);

  return (
    <div style={{ marginTop: "var(--ds-space-4)" }}>
      <div style={{
        fontFamily: "var(--ds-font-display)",
        fontSize: "var(--ds-text-lg)",
        fontWeight: 700,
        letterSpacing: "var(--ds-tracking-tight)",
        marginBottom: "var(--ds-space-3)",
      }}>
        {teamName(innings.battingTeamId)} — {formatScore(innings)} ({formatOvers(innings.overs)} ov)
      </div>

      {/* Batting table — horizontal scroll so all columns stay visible */}
      <div style={scrollWrap}>
        <table className="ds-table ds-table--bare ds-table--compact" style={{ minWidth: 340, marginBottom: "var(--ds-space-2)" }}>
          <thead>
            <tr>
              <th>Batter</th>
              <th>R</th>
              <th>B</th>
              <th>4s</th>
              <th>6s</th>
              <th>SR</th>
            </tr>
          </thead>
          <tbody>
            {batted.map((line) => (
              <tr key={line.playerId}>
                <td style={{ whiteSpace: "nowrap" }}>
                  {name(line.playerId)}
                  {line.isStriker ? " *" : ""}
                  {showStyle && line.hand !== null ? <span style={styleTag}>{battingHandShort(line.hand)}</span> : null}
                  <br />
                  <span style={{ fontSize: "var(--ds-text-2xs)", color: "var(--ds-text-muted)", fontWeight: 400 }}>
                    {formatBatterStatus(line, name)}
                  </span>
                </td>
                <td style={{ fontWeight: 700 }}>{line.runs}</td>
                <td>{line.balls}</td>
                <td>{line.fours}</td>
                <td>{line.sixes}</td>
                <td>{formatRate(line.strikeRate)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div style={{ fontSize: "var(--ds-text-xs)", color: "var(--ds-text-muted)", marginBottom: "var(--ds-space-1)" }}>
        Extras {formatExtras(innings.extras)}
      </div>
      {didNotBat.length === 0 ? null : (
        <div style={{ fontSize: "var(--ds-text-xs)", color: "var(--ds-text-muted)", marginBottom: "var(--ds-space-1)" }}>
          Did not bat: {didNotBat.map((line) => name(line.playerId)).join(", ")}
        </div>
      )}
      {innings.fallOfWickets.length === 0 ? null : (
        <div style={{ fontSize: "var(--ds-text-xs)", color: "var(--ds-text-muted)" }}>
          FoW: {innings.fallOfWickets.map((fow) => formatFallOfWicket(fow, name)).join(", ")}
        </div>
      )}

      {bowled.length === 0 ? null : (
        <div style={{ marginTop: "var(--ds-space-3)", paddingTop: "var(--ds-space-3)", borderTop: "1px solid var(--ds-border)" }}>
          <div style={{ fontSize: "var(--ds-text-sm)", fontWeight: 700, marginBottom: "var(--ds-space-2)" }}>
            Bowling
          </div>
          {/* Bowling table — horizontal scroll */}
          <div style={scrollWrap}>
            <table className="ds-table ds-table--bare ds-table--compact" style={{ minWidth: 280 }}>
              <tbody>
                {bowled.map((line) => (
                  <tr key={line.playerId}>
                    <td style={{ whiteSpace: "nowrap" }}>
                      {name(line.playerId)}
                      {showStyle && bowlingStyleShort(line.arm, line.type) !== null ? (
                        <span style={styleTag}>{bowlingStyleShort(line.arm, line.type)}</span>
                      ) : null}
                    </td>
                    <td style={{ whiteSpace: "nowrap" }}>{formatBowling(line)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

export function Scorecard({ state, name, teamName, open = false, showStyle = false }: Props): React.ReactElement {
  const current = state.innings[state.innings.length - 1];
  const partnership = state.header?.currentPartnership ?? null;

  return (
    <details
      className="ds-card"
      style={{ marginTop: "var(--ds-space-3)", overflow: "hidden" }}
      {...(open ? { open: true } : {})}
    >
      <summary style={{
        padding: "var(--ds-space-3) var(--ds-space-4)",
        display: "flex",
        alignItems: "center",
        gap: "var(--ds-space-2)",
        cursor: "pointer",
        fontWeight: 700,
        fontSize: "var(--ds-text-md)",
        listStyle: "none",
      }}>
        <span style={{ flex: 1 }}>Scorecard</span>
        {partnership !== null && (
          <span style={{ fontSize: "var(--ds-text-xs)", color: "var(--ds-text-muted)", fontWeight: 500 }}>
            P&apos;ship {partnership.runs} ({partnership.balls})
          </span>
        )}
        <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="var(--ds-text-muted)" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <path d="m6 9 6 6 6-6" />
        </svg>
      </summary>
      <div style={{ padding: "0 var(--ds-space-4) var(--ds-space-4)" }}>
        {state.result !== null && (
          <div style={{ fontWeight: 700, color: "var(--ds-success-600)", marginBottom: "var(--ds-space-2)" }}>
            {state.result.text}
          </div>
        )}
        {[...state.innings].reverse().map((innings) => (
          <Innings key={innings.inningsNo} innings={innings} name={name} teamName={teamName} showStyle={showStyle} />
        ))}
        {current === undefined && <div className="ds-caption">No innings yet</div>}
      </div>
    </details>
  );
}
